import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Collapse, IconButton, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ProductivityAppsWarningBanner from "./ProductivityAppsWarningBanner";

const OVERVIEW_BORDER = "#DDD9FF";
const OVERVIEW_BG = "linear-gradient(135deg, #F8F7FF 0%, #FFFFFF 72%)";

export type UserLifecycleKind = "none" | "onboarding" | "offboarding";

export type TicketAiOverviewPayload = {
  overview_lines: string[];
  /** From GenerateTicketAiOverview; one of none | onboarding | offboarding when server supports it */
  user_lifecycle?: string | null;
};

/** Notifies parent after each AI Overview fetch attempt (success or failure). */
export type OverviewFetchResult =
  | { ok: true; payload: TicketAiOverviewPayload }
  | { ok: false };

export interface AiOverviewProps {
  /** Tighter layout for embedded supervisor pane / extension */
  compact?: boolean;
  ticketId?: string | null;
  /** Free-form ticket text assembled by parent (fields, description, conversation, notes). */
  ticketContext?: string | null;
  /** The ticket's active Theo thread; its messages and tool calls are summarized too. */
  aiThreadId?: string | null;
  /** When false, skip fetching (e.g. Theo disabled for ticket). */
  fetchEnabled?: boolean;
  /**
   * Called when an overview fetch completes successfully or fails (for lifecycle + banner logic).
   */
  onOverviewFetchResult?: (result: OverviewFetchResult) => void;
  /**
   * Loads summary lines from Horizon / cortex.
   */
  fetchOverview: (
    ticketId: string,
    ticketContext: string,
    aiThreadId: string | null
  ) => Promise<TicketAiOverviewPayload>;
  /** Whether Microsoft 365 or Google Workspace is connected for the ticket customer. */
  hasProductivityApps?: boolean | null;
  /** Whether the ticket customer is mapped to a Theo customer. */
  customerConnectedToTheo?: boolean | null;
}

const listSx = (bodySize: number) => ({
  m: 0,
  pl: 2.25,
  fontFamily: "var(--theo-font-sans)",
  fontSize: bodySize,
  color: "#3F4A5A",
  lineHeight: 1.6,
  "& li": { mb: 0.75 },
});

/**
 * Summary of the ticket (with notes) and the Theo thread's work so far; loads async after mount when
 * context is available. Theo's later work shows up on the next load (e.g. page reload).
 */
export function AiOverview({
  compact = false,
  ticketId,
  ticketContext,
  aiThreadId = null,
  fetchEnabled = true,
  onOverviewFetchResult,
  fetchOverview,
  hasProductivityApps = null,
  customerConnectedToTheo = null,
}: AiOverviewProps) {
  const [expanded, setExpanded] = useState(true);
  const [lines, setLines] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRef = useRef(fetchOverview);
  fetchRef.current = fetchOverview;
  const onResultRef = useRef(onOverviewFetchResult);
  onResultRef.current = onOverviewFetchResult;

  const pad = compact ? 1.25 : 1.75;
  const titleSize = compact ? 13 : 14;
  const bodySize = compact ? 13 : 14;

  useEffect(() => {
    const tid = (ticketId || "").trim();
    const ctx = (ticketContext || "").trim();
    const threadId = (aiThreadId || "").trim() || null;

    if (!fetchEnabled || !tid || !ctx) {
      setLines(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setLines(null);

    fetchRef
      .current(tid, ctx, threadId)
      .then((payload) => {
        if (cancelled) return;
        const ol = payload?.overview_lines?.filter(Boolean) || [];
        setLines(ol.length ? ol : null);
        setLoading(false);
        onResultRef.current?.({
          ok: true,
          payload: {
            overview_lines: ol,
            user_lifecycle: payload?.user_lifecycle,
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn’t load summary.");
        setLoading(false);
        onResultRef.current?.({ ok: false });
      });

    return () => {
      cancelled = true;
    };
  }, [fetchEnabled, ticketId, ticketContext, aiThreadId]);

  const renderBody = () => {
    if (!fetchEnabled) {
      return (
        <Typography sx={{ fontSize: bodySize, color: "#9aa0a6", fontFamily: "var(--theo-font-sans)" }}>
          AI Overview is available when Theo chat is enabled for this ticket.
        </Typography>
      );
    }

    const ctx = (ticketContext || "").trim();
    if (!ctx) {
      return (
        <Typography sx={{ fontSize: bodySize, color: "#9aa0a6", fontFamily: "var(--theo-font-sans)" }}>
          Loading ticket details…
        </Typography>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          <CircularProgress size={compact ? 16 : 18} sx={{ color: "#8062D9" }} />
          <Typography sx={{ fontSize: bodySize, color: "#5F6B7A", fontFamily: "var(--theo-font-sans)" }}>
            Generating overview…
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography sx={{ fontSize: bodySize, color: "#c5221f", fontFamily: "var(--theo-font-sans)" }}>
          {error}
        </Typography>
      );
    }

    if (!lines || lines.length === 0) {
      return (
        <Typography sx={{ fontSize: bodySize, color: "#9aa0a6", fontFamily: "var(--theo-font-sans)" }}>
          No overview for this ticket yet — ask Theo in the chat below.
        </Typography>
      );
    }

    return (
      <Box component="ul" sx={listSx(bodySize)}>
        {lines.map((line, i) => (
          <li key={`l-${i}`}>{line}</li>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        flexShrink: 0,
        mb: 1.5,
        pt: 0.5,
      }}
    >
      <Box
        sx={{
          border: `1px solid ${OVERVIEW_BORDER}`,
          borderRadius: "8px",
          background: OVERVIEW_BG,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(67, 56, 168, 0.05)",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((e) => !e)}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: pad,
            py: compact ? 1 : 1.25,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            textAlign: "left",
            "&:hover": {
              backgroundColor: "rgba(128, 98, 217, 0.04)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.35,
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, mr: 0.25 }}>
                <Box sx={{ width: 14, height: 2, borderRadius: 1, bgcolor: "#8062D9" }} />
                <Box sx={{ width: 10, height: 2, borderRadius: 1, bgcolor: "#8062D9" }} />
                <Box sx={{ width: 16, height: 2, borderRadius: 1, bgcolor: "#8062D9" }} />
              </Box>
              <AutoAwesomeOutlinedIcon sx={{ fontSize: compact ? 16 : 18, color: "#8062D9" }} />
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: titleSize,
                fontWeight: 700,
                color: "#2F2A68",
                letterSpacing: "-0.01em",
              }}
            >
              AI Overview
            </Typography>
          </Box>
          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={expanded ? "Collapse AI Overview" : "Expand AI Overview"}
            sx={{ p: 0.25, color: "#6B7280" }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: compact ? 20 : 22 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: compact ? 20 : 22 }} />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: pad, pb: pad, pt: 0 }}>
            <ProductivityAppsWarningBanner
              hasProductivityApps={hasProductivityApps}
              customerConnectedToTheo={customerConnectedToTheo}
              compact={compact}
            />
            {renderBody()}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export default AiOverview;
